import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Alert, Button, Spinner, useToast } from '@parsina/framework';
import { C, soft } from '@parsina/framework/tokens';

import { useTranslation } from '../../i18n';
import { useClinicContext } from '../../context/ClinicContext.jsx';
import { StepHeading } from '../../components/auth/OnboardingParts.jsx';
import DoctorTrack from './DoctorTrack.jsx';
import { clinicEntryPath } from '../../lib/purchaseIntent.js';
import { describeError } from './errors.js';
import {
  STAGE, TRACK, clearQuickPlan, quickRun, readQuickPlan, resetQuickRun, startQuickSetup,
  subscribeQuickRun,
} from './quickSetup.js';

/**
 * «در حال آماده‌سازی پنل شما» — the run in quickSetup.js, drawn.
 *
 * Nothing on this screen asks for anything. It lists what is being done, in
 * order, and enters the clinic by itself when the last step is through. The
 * only controls are for a step that failed: the clinic could not be created
 * (try again, or go back), or the token could not be refreshed (try again, or
 * walk on — the clinic exists and the gate has states for a stale token).
 *
 * The steps after the clinic exists never stop the run. A role or «ویزیت» that
 * did not save is said once, on the way in, and is one screen away to fix.
 */

const STEP_ORDER = {
  [TRACK.DOCTOR]: [STAGE.DOCTOR, STAGE.CLINIC, STAGE.DEFAULTS, STAGE.LINK],
  [TRACK.CLINIC]: [STAGE.CLINIC, STAGE.DEFAULTS],
};

function StepIcon({ state }) {
  if (state === 'done') {
    return (
      <span style={{
        width: 26, height: 26, borderRadius: '50%', background: soft(C.teal, 0.14),
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <Check size={15} strokeWidth={3} color={C.teal} aria-hidden="true" />
      </span>
    );
  }
  if (state === 'running') return <Spinner size={20} />;
  return (
    <span style={{
      width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box',
      border: `2px solid ${state === 'failed' ? C.red : C.border}`,
    }}
    />
  );
}

export default function QuickSetup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    memberships, capabilities, refreshGrants, reload, repairSetup, defaultRef,
  } = useClinicContext();

  // The plan as «شروع» left it — read once, for the labels. The run clears it
  // when it finishes, and the labels must not vanish a moment before entering.
  const [plan] = React.useState(readQuickPlan);
  const [run, setRun] = React.useState(quickRun);
  const [prizeShown, setPrizeShown] = React.useState(false);

  React.useEffect(() => subscribeQuickRun(setRun), []);

  const start = React.useCallback((opts) => {
    startQuickSetup({ memberships, capabilities, refreshGrants, reload, repairSetup }, opts);
  }, [memberships, capabilities, refreshGrants, reload, repairSetup]);

  // Join the run in flight — this screen is remounted mid-run (quickSetup.js) —
  // or start it. With neither a run nor a plan there is nothing to resume: a
  // reload after the run ended lands in the clinic it built.
  React.useEffect(() => {
    if (quickRun()) return;
    if (!readQuickPlan()) {
      navigate(defaultRef ? `/c/${defaultRef}/` : '/start', { replace: true });
      return;
    }
    start();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const enter = React.useCallback(() => {
    const finished = quickRun();
    if (!finished?.done) return;
    resetQuickRun();   // first, so a second call finds nothing to do
    if (finished.created) toast?.success?.(t('auth.createClinic.created', { name: finished.clinicName }));
    if (finished.warnings.length) toast?.info?.(t('clinic.quickSetup.partial'));
    // «اشتراک» rather than the home, for someone who came to buy (lib/purchaseIntent.js).
    navigate(clinicEntryPath(finished.ref), { replace: true });
  }, [navigate, t, toast]);

  React.useEffect(() => {
    if (run?.done && !run.draw) enter();
  }, [run, enter]);

  const track = run?.track || plan?.track || TRACK.CLINIC;

  const abandon = () => {
    resetQuickRun();
    clearQuickPlan();
    navigate(track === TRACK.DOCTOR ? '/start' : '/onboarding?quick=1', { replace: true });
  };

  // ── The wheel, when a campaign owes one ──────────────────────────────────
  if (run?.done && run.draw) {
    return (
      <DoctorTrack
        phase="prize"
        draw={run.draw}
        onPrizeDone={() => { setPrizeShown(true); enter(); }}
        disabled={prizeShown}
      />
    );
  }

  const order = STEP_ORDER[track] || STEP_ORDER[TRACK.CLINIC];
  const at = run?.done ? order.length : Math.max(order.indexOf(run?.stage), 0);
  const failed = Boolean(run?.error);
  const clinicName = run?.clinicName || plan?.clinicName || '';
  const detail = {
    [STAGE.DOCTOR]: [plan?.doctorName, plan?.specialty].filter(Boolean).join(' — '),
    [STAGE.CLINIC]: clinicName ? `«${clinicName}»` : '',
    [STAGE.DEFAULTS]: t(track === TRACK.DOCTOR ? 'clinic.quickSetup.details.defaultsDoctor' : 'clinic.quickSetup.details.defaultsClinic'),
    [STAGE.LINK]: t('clinic.quickSetup.details.link'),
  };
  const steps = order.map((key, i) => {
    // The doctor's record was written on «شروع», before this screen opened.
    let state = 'waiting';
    if (key === STAGE.DOCTOR || i < at) state = 'done';
    else if (i === at) state = failed ? 'failed' : 'running';
    return { key, state, label: t(`clinic.quickSetup.steps.${key}`), detail: detail[key] };
  });

  // What failed, and which ways out it has.
  const createFailed = failed && run.error.step === 'create';
  const accessFailed = failed && run.error.step === 'access';
  const message = !failed ? ''
    : createFailed ? describeError(run.error.err, 'createClinic', t).message
      : accessFailed ? t('auth.createClinic.refreshFailed')
        : (run.error.err?.message || t('errors.state.saveFailed'));

  return (
    <div data-testid="quick-setup">
      <StepHeading title={t('clinic.quickSetup.title')} subtitle={t('clinic.quickSetup.body')} />

      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-label={t('clinic.quickSetup.title')}>
        {steps.map((s) => (
          <li
            key={s.key}
            data-testid={`quick-step-${s.key}`}
            data-state={s.state}
            style={{
              display: 'flex', gap: 12, alignItems: 'flex-start', padding: '11px 0',
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <span style={{ width: 26, height: 26, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StepIcon state={s.state} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                display: 'block', fontSize: '0.84375rem', fontWeight: 700, lineHeight: 1.7,
                color: s.state === 'waiting' ? C.textSecondary : C.textPrimary,
              }}
              >
                {s.label}
              </span>
              {s.detail ? (
                <span style={{ display: 'block', fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.7 }}>{s.detail}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>

      {failed ? (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Alert variant="error" data-testid="quick-setup-error">{message}</Alert>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button onClick={() => start()} data-testid="quick-setup-retry">
              {t('common.actions.retry')}
            </Button>
            {accessFailed ? (
              <Button variant="outline" onClick={() => start({ skipAccess: true })} data-testid="quick-setup-proceed">
                {t('auth.createClinic.continueAnyway')}
              </Button>
            ) : null}
            {createFailed ? (
              <Button variant="outline" onClick={abandon} data-testid="quick-setup-back">
                {t('common.actions.back')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <span className="sr-only" aria-live="polite">
        {failed ? message : (steps.find((s) => s.state === 'running')?.label || '')}
      </span>
    </div>
  );
}
